import type { Permission } from '@mntn-dev/authorization-types'
import { runningOnServer } from '@mntn-dev/utilities'
import type { ExactObject } from '@mntn-dev/utility-types'
import type { EmptyObject } from 'type-fest'
import type { UriKind } from './types/url-types.ts'

type Permissions = Permission[][]
export type RouteAttributes = { permissions?: Permissions }

export type RouteRegistration<
  TPattern extends string,
  TRouteParams extends {} = EmptyObject,
  TQueryParams extends {} = EmptyObject,
> = {
  pattern: TPattern
  params: (
    routeParams: ExactObject<TRouteParams>
  ) => RouteRegistration<TPattern, TRouteParams, TQueryParams>
  permissions: Permissions
  query: (
    queryParams: ExactObject<TQueryParams>
  ) => RouteRegistration<TPattern, TRouteParams, TQueryParams>
  toRelativeUrl: () => string
  toAbsoluteUrl: () => string
  toUrl: (uriKind: UriKind) => string
  isMatch: (otherPath: string) => boolean
  isProtected: boolean
  isTest: boolean
  getRoot: () => string
}

export function register<TPattern extends string>(pattern: TPattern) {
  // route registration state
  const registrationState: {
    permissions: Permissions
    isProtected: boolean
    isTest: boolean
  } = {
    permissions: [],
    isProtected: false,
    isTest: false,
  }

  const registration = {
    protected: (withPermissions?: Permissions) => {
      registrationState.permissions = withPermissions ?? []
      registrationState.isProtected = true
      return registration
    },
    test: () => {
      registrationState.isTest = true
      return registration
    },
    toRoute: <
      TRouteParams extends {} = EmptyObject,
      TQueryParams extends {} = EmptyObject,
    >(): [
      TPattern,
      () => RouteRegistration<TPattern, TRouteParams, TQueryParams>,
    ] => {
      const routeBuilder: () => RouteRegistration<
        TPattern,
        TRouteParams,
        TQueryParams
      > = () => {
        let path: string = pattern
        const toRelativeUrl = () => path
        const toAbsoluteUrl = () =>
          // TODO: Move getClientBaseUrl function from magicsky to somewhere app & env common and hook into it from here.
          // Maybe a new package called app-common?
          // Ensure Storybook Activity still works. It seems to have issue with env from @mntn-dev/env
          `${runningOnServer() ? process.env.BASE_URL : window.location.origin}${path}`

        const route: RouteRegistration<TPattern, TRouteParams, TQueryParams> = {
          pattern,
          params: (routeParams) => {
            path = Object.keys(routeParams).reduce<string>((path, key) => {
              return path.replace(
                `:${key}`,
                `${routeParams[key as keyof ExactObject<TRouteParams>]}`
              )
            }, path)

            return route
          },
          query: (queryParams) => {
            path = `${path}?${new URLSearchParams(
              Object.entries(queryParams)
                .map(([key, value]) => [
                  key,
                  value === undefined ? '' : String(value),
                ])
                .sort()
            ).toString()}`

            return route
          },
          ...registrationState,
          toRelativeUrl,
          toAbsoluteUrl,
          toUrl: (uriKind) =>
            uriKind === 'relative' ? toRelativeUrl() : toAbsoluteUrl(),
          isMatch: (otherPath: string) => {
            const otherPathSegments = otherPath.split('/')
            const thisPathSegments = path.split('/')

            if (otherPathSegments.length !== thisPathSegments.length) {
              return false
            }

            return thisPathSegments.reduce<boolean>((result, _, index) => {
              if (result) {
                return (
                  thisPathSegments[index]?.startsWith(':') ||
                  otherPathSegments[index] === thisPathSegments[index]
                )
              }
              return false
            }, true)
          },
          getRoot: () => `/${pattern.split('/')[1] ?? ''}`,
        }

        return route
      }

      return [pattern, routeBuilder]
    },
  }

  return registration
}
